import { css } from '@emotion/react'
import { useStaticQuery, graphql } from 'gatsby'
import React, { ReactNode } from 'react'
import { mq, rem } from '../../styles/mixin.styles'
import { breakpoints, colors, fontSize, space } from '../../styles/variables.styles'
import { getPathName, isSinglePathPage } from '../../utils/customPath'
import { Aside } from '../global/Aside'
import { Layout } from '../global/Layout'
import { Main } from '../global/Main'
import { Seo } from '../global/Seo'
import { Breadcrumb } from './Breadcrumb'
import { PageTitle } from './PageTitle'

type Props = {
  slug: string
  children: ReactNode
  readText?: ReactNode
  subRead?: string
  subTitle?: string
  description?: string
  pathname: string
}

/**
 * Single
 *
 * @returns single page contents.
 */
const Single = ({ slug, children, readText, subRead, subTitle, description, pathname }: Props) => {
  const data = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `)
  const title = getPathName(slug)
  const isSimple = isSinglePathPage(slug)
  const pageDescription = `iPaSの${title}についてのページです。iPaSはパレットの荷姿構想から調達までの一連のプロセスを支援し一元管理できるシステムです。パレット調達の業務負荷を軽減し、荷姿品質の向上と調達コストの削減を実現します。`
  const canonical = `${data.site.siteMetadata.siteUrl + pathname}`

  return (
    <Layout>
      <Main>
        <Seo
          title={title}
          description={pageDescription}
          openGraph={{ description: pageDescription }}
          canonical={canonical}
        />
        <article css={pageStyles}>
          <PageTitle
            title={title}
            subtitle={subTitle ? subTitle : slug}
            readText={readText}
            subRead={subRead}
            isSimple={isSimple}
            description={description}
          />
          {isSimple ? children : <div className="page-body">{children}</div>}
        </article>
      </Main>
      {/* お問い合わせページでのみコンタクトエリアを非表示 */}
      {slug !== 'contact' && <Aside />}
      <Breadcrumb slug={slug} />
    </Layout>
  )
}

export default Single

const pageStyles = css`
  .page-body {
    width: calc(100% - (${space.md} * 2));
    max-width: ${rem(breakpoints.minContent)};
    margin: 0 auto;
    h2 {
      color: ${colors.mainBlue.hex};
      position: relative;
      display: gap;
      margin-top: clamp(${space.lg}, 3vw, calc(${space.xxl} * 2));
      + h3,
      + dl {
        margin-top: clamp(${space.md}, 3vw, ${space.lg});
      }
      + h3 {
        + p {
          line-height: 2;
          margin-top: clamp(${space.xs}, 1vw, ${space.lg});
        }
      }
    }
    .option-title {
      background-color: ${colors.mainBlue.hex};
      display: flex;
      width: max-content;
      padding: 0 ${space.md};
      margin-top: clamp(${rem(50)}, 5vw, ${rem(80)});
      font-size: ${fontSize.lg};
      color: ${colors.white.hex};
      border-radius: 50em;
      + div {
        margin-top: ${rem(25)};
      }
    }
  }
  ${mq('sm')} {
    .page-body {
      width: min(84vw, ${rem(breakpoints.minContent)});
      margin-right: ${space.lg};
      h2 {
        margin-top: calc(${space.xxl} * 2);
        &::before {
          transform: skew(-15deg, 0deg) translate(${rem(8)}, ${rem(-2)});
        }
        + h3 {
          + p {
            line-height: 2;
            margin-top: ${rem(30)};
          }
        }
      }
      .option-title {
        margin-top: calc(${space.xxl} * 2);
      }
    }
  }
  ${mq('lg')} {
    .page-body {
      margin-right: 0;
    }
  }
`
